import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../api';
import {
  PageHeader, Loading, ErrorBanner, Empty, Badge, Modal, ui, fmtDate,
} from '../components/adminUi';

const ROLE_LABELS = { admin: 'Admin', provider: 'Usta', client: 'Mijoz' };
const ROLE_TONE = { admin: 'amber', provider: 'blue', client: 'green' };

export default function UsersPage() {
  const { auth } = useAuth();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [actingId, setActingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      if (query.trim()) params.set('search', query.trim());
      if (roleFilter) params.set('role', roleFilter);
      const res = await apiFetch(`/api/admin/users?${params.toString()}`, auth.token);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Number(data.total) || 0);
      if (data.pageSize) setPageSize(Number(data.pageSize));
    } catch {
      setItems([]);
      setFetchError('Foydalanuvchilarni yuklashda xatolik yuz berdi. Sahifani yangilang.');
    } finally {
      setLoading(false);
    }
  }, [auth.token, page, query, roleFilter]);

  useEffect(() => { load(); }, [load]);

  const submitSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setQuery(search);
  };

  const openProfile = async (id) => {
    setProfile({ id, __loading: true });
    setProfileLoading(true);
    try {
      const res = await apiFetch(`/api/admin/users/${id}`, auth.token);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setProfile(data);
    } catch {
      setProfile({ id, __error: true });
    } finally {
      setProfileLoading(false);
    }
  };

  const toggleBlock = async (user) => {
    const action = user.isBlocked ? 'unblock' : 'block';
    setActingId(user.id);
    try {
      const res = await apiFetch(`/api/admin/users/${user.id}/${action}`, auth.token, {
        method: 'POST',
      });
      if (!res.ok) throw new Error();
      // Reflect the new state locally, then refresh from server.
      setItems((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, isBlocked: !user.isBlocked } : u))
      );
      if (profile && profile.id === user.id) {
        setProfile((p) => ({ ...p, isBlocked: !user.isBlocked }));
      }
      await load();
    } catch {
      setFetchError('Amalni bajarishda xatolik yuz berdi.');
    } finally {
      setActingId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / (pageSize || 20)));

  return (
    <AdminLayout>
      <PageHeader title="Foydalanuvchilar" subtitle="Barcha ro'yxatdan o'tgan foydalanuvchilar">
        <form onSubmit={submitSearch} style={{ display: 'flex', gap: '8px' }}>
          <input
            style={ui.searchInput}
            placeholder="Ism yoki telefon bo'yicha qidirish..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            style={{ ...ui.select, width: 'auto', minWidth: '130px' }}
            value={roleFilter}
            onChange={(e) => { setPage(1); setRoleFilter(e.target.value); }}
          >
            <option value="">Barcha rollar</option>
            <option value="client">Mijoz</option>
            <option value="provider">Usta</option>
            <option value="admin">Admin</option>
          </select>
          <button type="submit" style={ui.addBtn}>Qidirish</button>
        </form>
      </PageHeader>

      <ErrorBanner>{fetchError}</ErrorBanner>

      {loading ? (
        <Loading />
      ) : items.length === 0 ? (
        <Empty icon="👥" title="Foydalanuvchi topilmadi" hint="Qidiruv yoki filtrni o'zgartirib ko'ring." />
      ) : (
        <>
          <div style={ui.tableWrap}>
            <table style={ui.table}>
              <thead>
                <tr>
                  <th style={{ ...ui.th, width: '48px' }}>#</th>
                  <th style={ui.th}>F.I.O</th>
                  <th style={ui.th}>Telefon</th>
                  <th style={ui.th}>Rol</th>
                  <th style={ui.th}>Holat</th>
                  <th style={ui.th}>Ro'yxatdan o'tgan</th>
                  <th style={{ ...ui.th, textAlign: 'right' }}>Amallar</th>
                </tr>
              </thead>
              <tbody>
                {items.map((u, i) => (
                  <tr key={u.id} style={ui.tr}>
                    <td style={{ ...ui.td, color: 'var(--text-faint)' }}>{(page - 1) * pageSize + i + 1}</td>
                    <td style={{ ...ui.td, fontWeight: '600', color: 'var(--text)' }}>
                      {[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td style={ui.td}>{u.phone || '—'}</td>
                    <td style={ui.td}>
                      <Badge tone={ROLE_TONE[u.role] || 'gray'}>{ROLE_LABELS[u.role] || u.role || '—'}</Badge>
                    </td>
                    <td style={ui.td}>
                      {u.isBlocked
                        ? <Badge tone="red">Bloklangan</Badge>
                        : <Badge tone="green">Faol</Badge>}
                    </td>
                    <td style={ui.td}>{fmtDate(u.createdAt)}</td>
                    <td style={{ ...ui.td, textAlign: 'right' }}>
                      <div style={ui.actions}>
                        <button
                          style={{ ...ui.btnSm, ...ui.btnBlue }}
                          onClick={() => openProfile(u.id)}
                        >
                          Profil
                        </button>
                        {u.role !== 'admin' && (
                          <button
                            style={{
                              ...ui.btnSm,
                              ...(u.isBlocked ? ui.btnGreen : ui.btnRed),
                              ...(actingId === u.id ? ui.btnDisabled : {}),
                            }}
                            onClick={() => toggleBlock(u)}
                            disabled={actingId === u.id}
                          >
                            {actingId === u.id
                              ? '...'
                              : u.isBlocked ? 'Blokdan chiqarish' : 'Bloklash'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={ui.pager}>
            <button
              style={{ ...ui.pagerBtn, ...(page <= 1 ? ui.btnDisabled : {}) }}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              ← Oldingi
            </button>
            <span style={ui.pagerInfo}>{page} / {totalPages} · jami {total} ta</span>
            <button
              style={{ ...ui.pagerBtn, ...(page >= totalPages ? ui.btnDisabled : {}) }}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Keyingi →
            </button>
          </div>
        </>
      )}

      {profile && (
        <Modal title="Foydalanuvchi profili" onClose={() => setProfile(null)} maxWidth="460px">
          {profileLoading || profile.__loading ? (
            <Loading />
          ) : profile.__error ? (
            <ErrorBanner>Profilni yuklab bo'lmadi.</ErrorBanner>
          ) : (
            <>
              <div style={pf.head}>
                <div style={pf.avatar}>
                  {(profile.firstName || '?').slice(0, 1).toUpperCase()}
                </div>
                <div>
                  <div style={pf.name}>
                    {[profile.firstName, profile.lastName].filter(Boolean).join(' ') || '—'}
                  </div>
                  <div style={pf.sub}>
                    <Badge tone={ROLE_TONE[profile.role] || 'gray'}>
                      {ROLE_LABELS[profile.role] || profile.role || '—'}
                    </Badge>
                    {profile.isBlocked
                      ? <Badge tone="red">Bloklangan</Badge>
                      : <Badge tone="green">Faol</Badge>}
                  </div>
                </div>
              </div>
              <Row label="Telefon" value={profile.phone} />
              <Row label="Login" value={profile.login} />
              <Row label="Manzil" value={profile.address} />
              <Row label="Holat (status)" value={profile.status} />
              <Row label="Moderatsiya" value={profile.moderationStatus} />
              <Row label="MyID tasdiqlangan" value={profile.myidVerified ? 'Ha' : "Yo'q"} />
              <Row label="Ro'yxatdan o'tgan" value={fmtDate(profile.createdAt, true)} />

              {profile.role !== 'admin' && (
                <div style={ui.modalActions}>
                  <button
                    style={{
                      ...(profile.isBlocked ? ui.saveBtn : { ...ui.saveBtn, background: 'var(--danger)' }),
                      ...(actingId === profile.id ? ui.btnDisabled : {}),
                    }}
                    onClick={() => toggleBlock(profile)}
                    disabled={actingId === profile.id}
                  >
                    {actingId === profile.id
                      ? 'Bajarilmoqda...'
                      : profile.isBlocked ? 'Blokdan chiqarish' : 'Bloklash'}
                  </button>
                </div>
              )}
            </>
          )}
        </Modal>
      )}
    </AdminLayout>
  );
}

function Row({ label, value }) {
  return (
    <div style={pf.row}>
      <span style={pf.rowLabel}>{label}</span>
      <span style={pf.rowValue}>{value === null || value === undefined || value === '' ? '—' : value}</span>
    </div>
  );
}

const pf = {
  head: { display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '18px' },
  avatar: {
    width: '52px',
    height: '52px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #e94560, #0f3460)',
    color: 'var(--on-accent)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '22px',
    fontWeight: '700',
    flexShrink: 0,
  },
  name: { fontSize: '17px', fontWeight: '700', color: 'var(--text)', marginBottom: '6px' },
  sub: { display: 'flex', gap: '6px' },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '9px 0',
    borderBottom: '1px solid #f3f4f6',
    fontSize: '14px',
  },
  rowLabel: { color: 'var(--text-muted)' },
  rowValue: { color: 'var(--text)', fontWeight: '500', textAlign: 'right' },
};
